/**
 * Système de notifications global — toasts qui s'empilent en haut à droite
 * (en bas sur mobile), auto-dismiss 4 s, pause au survol.
 *
 * Usage :
 *   const notify = useNotify();
 *   notify.info("Session reconnectée");
 *   notify.warn("Hôte déconnecté — reconnexion en cours");
 *   notify.danger("Échec d'envoi", { sticky: true });
 *
 * Pourquoi un système global : avant, chaque panel exposait son
 * `panel.status()` local. Pas de canal pour signaler "Host décroché",
 * "Plugin mis à jour", "Session reconnectée" quand l'opérateur est sur
 * un autre panel — l'évent passe inaperçu.
 */
import { createContext, createSignal, For, onCleanup, Show, useContext, type Component, type JSX } from "solid-js";
import "./notify.css";

export type NotifyLevel = "ok" | "info" | "warn" | "danger";

export interface NotifyOpts {
  /** Niveau / intent — change la couleur du bord et l'icône. */
  level?: NotifyLevel;
  /** Durée en ms avant auto-dismiss. 0 ou true = sticky (jamais dismissé seul). */
  durationMs?: number;
  /** Si vrai : le toast reste affiché jusqu'à clic explicite. */
  sticky?: boolean;
  /** Sous-titre optionnel (1 ligne, sous le message principal). */
  hint?: string;
  /** Action principale (bouton à droite). Cliquée → action() + dismiss. */
  action?: { label: string; run: () => void };
}

export interface NotifyItem extends Required<Pick<NotifyOpts, "level">> {
  id: number;
  message: string;
  hint?: string;
  sticky: boolean;
  action?: NotifyOpts["action"];
  /** Timestamp d'apparition pour calculer le restant à la pause. */
  createdAt: number;
}

export interface NotifyApi {
  notify: (message: string, opts?: NotifyOpts) => number;
  ok: (m: string, o?: Omit<NotifyOpts, "level">) => number;
  info: (m: string, o?: Omit<NotifyOpts, "level">) => number;
  warn: (m: string, o?: Omit<NotifyOpts, "level">) => number;
  danger: (m: string, o?: Omit<NotifyOpts, "level">) => number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

const NotifyContext = createContext<NotifyApi | null>(null);

const DEFAULT_DURATION_MS = 4_000;
const MAX_STACKED = 5;

export const NotifyProvider: Component<{ children: JSX.Element }> = (props) => {
  const [items, setItems] = createSignal<NotifyItem[]>([]);
  // Pour chaque id, le setTimeout actif (clearable au hover / dismiss).
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  let nextId = 1;

  function dismiss(id: number) {
    const t = timers.get(id);
    if (t) clearTimeout(t);
    timers.delete(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }
  function dismissAll() {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    setItems([]);
  }
  function schedule(id: number, ms: number) {
    if (ms <= 0) return;
    const t = setTimeout(() => dismiss(id), ms);
    timers.set(id, t);
  }
  function clearScheduled(id: number) {
    const t = timers.get(id);
    if (t) {
      clearTimeout(t);
      timers.delete(id);
    }
  }
  function notify(message: string, opts: NotifyOpts = {}): number {
    const id = nextId++;
    const level: NotifyLevel = opts.level ?? "info";
    const sticky = Boolean(opts.sticky) || opts.durationMs === 0;
    const durationMs = opts.durationMs ?? DEFAULT_DURATION_MS;
    const item: NotifyItem = {
      id,
      level,
      message,
      hint: opts.hint,
      sticky,
      action: opts.action,
      createdAt: Date.now(),
    };
    setItems((prev) => {
      // Cap à MAX_STACKED — l'élément le plus ancien (non sticky) saute.
      const next = [item, ...prev];
      if (next.length <= MAX_STACKED) return next;
      // Trouver le premier non-sticky en partant de la fin → dégage.
      for (let i = next.length - 1; i >= 0; i--) {
        if (!next[i].sticky) {
          const dropped = next[i];
          const dt = timers.get(dropped.id);
          if (dt) clearTimeout(dt);
          timers.delete(dropped.id);
          next.splice(i, 1);
          break;
        }
      }
      return next.slice(0, MAX_STACKED);
    });
    if (!sticky) schedule(id, durationMs);
    return id;
  }
  // Helpers par niveau.
  const ok = (m: string, o?: Omit<NotifyOpts, "level">) => notify(m, { ...o, level: "ok" });
  const info = (m: string, o?: Omit<NotifyOpts, "level">) => notify(m, { ...o, level: "info" });
  const warn = (m: string, o?: Omit<NotifyOpts, "level">) => notify(m, { ...o, level: "warn" });
  const danger = (m: string, o?: Omit<NotifyOpts, "level">) => notify(m, { ...o, level: "danger" });

  const api: NotifyApi = { notify, ok, info, warn, danger, dismiss, dismissAll };
  onCleanup(dismissAll);

  return (
    <NotifyContext.Provider value={api}>
      {props.children}
      <NotificationCenter
        items={items}
        onDismiss={dismiss}
        onPauseStart={(id) => clearScheduled(id)}
        onPauseEnd={(item) => {
          if (item.sticky) return;
          // Calcule le restant à partir de createdAt → reschedule.
          const elapsed = Date.now() - item.createdAt;
          const remaining = Math.max(800, DEFAULT_DURATION_MS - elapsed);
          schedule(item.id, remaining);
        }}
      />
    </NotifyContext.Provider>
  );
};

export function useNotify(): NotifyApi {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error("useNotify() utilisé hors d'un <NotifyProvider>");
  return ctx;
}

// ─── NotificationCenter (rendu) ──────────────────────────────────────────

const LEVEL_ICON: Record<NotifyLevel, string> = {
  ok: "✓",
  info: "ⓘ",
  warn: "⚠",
  danger: "✕",
};

const NotificationCenter: Component<{
  items: () => NotifyItem[];
  onDismiss: (id: number) => void;
  onPauseStart: (id: number) => void;
  onPauseEnd: (item: NotifyItem) => void;
}> = (props) => {
  return (
    <Show when={props.items().length > 0}>
      <div class="notify-center" role="region" aria-label="Notifications" aria-live="polite">
        <For each={props.items()}>
          {(item) => (
            <div
              class={`notify notify--${item.level}`}
              role={item.level === "danger" ? "alert" : "status"}
              onMouseEnter={() => props.onPauseStart(item.id)}
              onMouseLeave={() => props.onPauseEnd(item)}
            >
              <span class="notify__icon" aria-hidden="true">
                {LEVEL_ICON[item.level]}
              </span>
              <div class="notify__body">
                <div class="notify__message">{item.message}</div>
                <Show when={item.hint}>
                  <div class="notify__hint">{item.hint}</div>
                </Show>
              </div>
              <Show when={item.action}>
                {(act) => (
                  <button
                    type="button"
                    class="btn btn--xs notify__action"
                    onClick={() => {
                      act().run();
                      props.onDismiss(item.id);
                    }}
                  >
                    {act().label}
                  </button>
                )}
              </Show>
              <button
                type="button"
                class="notify__close"
                aria-label="Fermer la notification"
                onClick={() => props.onDismiss(item.id)}
              >
                ✕
              </button>
            </div>
          )}
        </For>
      </div>
    </Show>
  );
};
